import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  DATE_PICKER_FORMAT,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  today,
  weekBeforeToday,
} from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type {
  filtersFormValues,
  ReadLogsModalTypes,
} from '@app/pages/SalesFunnels/LogsRegistry/types';
import { usePagination } from '@app/pages/Sendouts/hooks/usePagination';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import {
  useLazyGetLogsSearchResultQuery,
  useLogsMarkAsReadMutation,
  useSearchLogsMutation,
} from '@app/services/rtkQuery/SalesFunnelsStages/api';
import type { NSalesFunnelsStagesApi } from '@app/services/rtkQuery/SalesFunnelsStages/types';

const { useForm } = Form;

export const useLogsRegistry = () => {
  const location = useLocation();

  const salesFunnelId = location.state?.salesFunnelId;
  const robotId = location.state?.robotId;

  const { activeBranchId } = useUserPrint();

  const [filtersForm] = useForm();

  const {
    rowsPerPage,
    pageNumber,
    totalRowsCount,
    change: changePagination,
    setTotalRowsCount,
  } = usePagination();

  const defaultSearchLogsParams = useMemo(() => {
    const data = {
      ActiveBranchId: activeBranchId,
      data: {
        RobotIds: robotId ? [Number(robotId)] : null,
        SalesFunnelIds: salesFunnelId ? [Number(salesFunnelId)] : null,
        SearchTerm: null,
        DateS: weekBeforeToday.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
        DateE: today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
        IsRead: null,
        LeadIds: null,
        ItemsPerPage: 20,
      },
    };

    return data;
  }, [activeBranchId, robotId, salesFunnelId]);

  const [searchLogsParams, setSearchLogsParams] =
    useState<NSalesFunnelsStagesApi.SearchLogsRequest>(defaultSearchLogsParams);
  const [logsData, setLogsData] = useState<
    NSalesFunnelsStagesApi.ModelRobotLog[]
  >([]);
  const [searchId, setSearchId] = useState(0);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalDescription, setMessageModalDescription] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [readLogsModalType, setReadLogsModalType] =
    useState<ReadLogsModalTypes | null>(null);
  const [markAsReadModalOpen, setMarkAsReadModalOpen] = useState(false);

  const { showNotification } = useNotification();

  const [searchLogs, { isLoading: isSearchLogsLoading }] =
    useSearchLogsMutation();
  const [getLogsSearchResult, { isLoading: isGetLogsSearchResult }] =
    useLazyGetLogsSearchResultQuery();
  const [logsMarkAsRead, { isLoading: isLogsMarkAsRead }] =
    useLogsMarkAsReadMutation();

  const handleSearchLogs = useCallback(
    async (data: NSalesFunnelsStagesApi.SearchLogsRequest) => {
      try {
        const { Items, Id, CountItems, PageN } = await searchLogs(
          data
        ).unwrap();

        setLogsData(Items || []);
        setTotalRowsCount(CountItems);
        changePagination(PageN, 20);
        setSearchId(Id);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    // changePagination в зависимостях вызывает лишние вызовы
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showNotification, searchLogs, setTotalRowsCount]
  );

  useEffect(() => {
    handleSearchLogs(defaultSearchLogsParams);
  }, [defaultSearchLogsParams, handleSearchLogs]);

  const resetFilters = () => {
    filtersForm.resetFields();
    setSearchLogsParams(defaultSearchLogsParams);
    handleSearchLogs(defaultSearchLogsParams);
  };

  const handleTableChange = async (page: number, pageSize: number) => {
    try {
      const { Items } = await getLogsSearchResult({
        ActiveBranchId: activeBranchId,
        searchId,
        page,
        rowsPerPage: pageSize,
      }).unwrap();

      setLogsData(Items || []);
      changePagination(page, pageSize);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  const handleSubmitFilterForm = async () => {
    const {
      Date,
      SearchTerm,
      IsUnread,
      SalesFunnelIds,
      RobotIds,
      LeadIds,
    }: filtersFormValues = filtersForm.getFieldsValue();

    const [start, end] = Date || [];

    if (!start || !end) {
      return showNotification(
        'Пожалуйста, заполните обе даты в фильтре периода',
        FRONT_WARNING
      );
    }

    const DateS = start?.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]) || '';
    const DateE = end?.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]) || '';

    const filtersData = {
      ...searchLogsParams,
      data: {
        ...searchLogsParams.data,
        SearchTerm,
        DateS,
        DateE,
        IsRead: !IsUnread,
        LeadIds: LeadIds ? [LeadIds] : null,
        SalesFunnelIds: SalesFunnelIds ? [SalesFunnelIds] : null,
        RobotIds: RobotIds ? [RobotIds] : null,
        ItemsPerPage: rowsPerPage,
      },
    };

    handleSearchLogs(filtersData);
    setSearchLogsParams(filtersData);
  };

  const handleMessageModalOpen = (message: string | null) => {
    if (!message) return;

    setMessageModalDescription(message);
    setMessageModalOpen(true);
  };

  const handleMessageModalClose = () => {
    setMessageModalDescription('');
    setMessageModalOpen(false);
  };

  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const openMarkAsReadModal = (markType: ReadLogsModalTypes) => {
    setReadLogsModalType(markType);
    setMarkAsReadModalOpen(true);
  };

  const handleMarkAsReadModalCancel = () => {
    setMarkAsReadModalOpen(false);
    setReadLogsModalType(null);
  };

  const handleMarkAsRead = async () => {
    try {
      if (readLogsModalType)
        await logsMarkAsRead({
          ActiveBranchId: activeBranchId,
          data: {
            MarkType: readLogsModalType,
            Filter: searchLogsParams.data,
            LogIds:
              (selectedRowKeys as string[]).filter(Boolean).map(Number) ?? [],
          },
        }).unwrap();

      handleMarkAsReadModalCancel();
      handleSearchLogs(searchLogsParams);
      setSelectedRowKeys([]);
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  };

  return {
    isSearchLogsLoading,
    logsData,
    rowsPerPage,
    pageNumber,
    totalRowsCount,
    resetFilters,
    filtersForm,
    handleTableChange,
    isGetLogsSearchResult,
    searchLogsParams,
    handleSubmitFilterForm,
    handleMessageModalOpen,
    messageModalOpen,
    messageModalDescription,
    handleMessageModalClose,
    rowSelection,
    selectionCount: selectedRowKeys.length || 0,
    readLogsModalType,
    markAsReadModalOpen,
    handleMarkAsReadModalCancel,
    handleMarkAsRead,
    openMarkAsReadModal,
    isLogsMarkAsRead,
  };
};
